import { ScrollArea } from '@/components/ui/scroll-area'
import { useAi } from '@/context/ai'
import { useBookmarkItems } from '@/context/bookmarkedItems'
import type { ArticleCard as ArticleCardType } from '@/shared/api-generated-types'
import ArticleCard from '@/widgets/v2/AI/Cards/Article'
import SkeletonCard from '@/widgets/v2/AI/Cards/Skeleton'
import { uniqBy } from 'lodash'
import { useEffect } from 'react'
import { gql, useQuery } from 'urql'

const MAX_RECOMMENDATIONS = 20

const FragArticleCard = gql`
  fragment ArticleCard on ArticleCard {
    id
    slug
    title
    guideName
    guideAvatarUrl
    guideId
    imageUrl
    readTimeInSeconds
    bookmarked
  }
`

const QUERY_SEARCH_AI_ARTICLES = gql`
  ${FragArticleCard}

  query SearchAiArticles($input: AiSearchInput!) {
    searchAiArticles(input: $input) {
      items {
        ...ArticleCard
      }
    }
  }
`

interface Props {
  height: number
}

const RelatedArticles = (props: Props): JSX.Element => {
  const {
    state: { query: searchQuery, relatedArticles },
    dispatch,
  } = useAi()
  const { state: bookmarkState } = useBookmarkItems()

  const [{ data, fetching }] = useQuery<{
    searchAiArticles: { items: ArticleCardType[] }
  }>({
    query: QUERY_SEARCH_AI_ARTICLES,
    pause: searchQuery.trim() === '',
    variables: { input: { query: searchQuery, limit: 5 } },
    requestPolicy: 'cache-first',
  })

  useEffect(() => {
    if (fetching || !data || !data.searchAiArticles?.items.length) return
    data.searchAiArticles.items.map((item) => relatedArticles.unshift(item))
    dispatch({
      type: 'UPDATE',
      payload: { relatedArticles: uniqBy(relatedArticles, 'id').slice(0, MAX_RECOMMENDATIONS) },
    })
  }, [data, fetching])

  return (
    <ScrollArea
      style={{
        height: `${props.height * (relatedArticles.length || 1) * 0.2}px`,
        maxHeight: `${props.height * 0.75}px`,
      }}
    >
      {fetching && <SkeletonCard />}
      {relatedArticles.map((article: ArticleCardType) => (
        <ArticleCard
          key={article.id}
          image={article.imageUrl}
          {...article}
          bookmarked={
            bookmarkState.articles?.[article.id] !== undefined
              ? bookmarkState.articles?.[article.id]
              : article.bookmarked
          }
        />
      ))}
    </ScrollArea>
  )
}

export default RelatedArticles
